import { Skeleton as AntdSkeleton } from 'antd';
import { forwardRef, useEffect, useRef } from 'react';

import { Button } from 'components/general/Button';
import { UserRenderer } from 'components/table/UserRenderer';

interface SuggestionListProps {
  items: Array<{ id: string; name: string }>;
  command: (item: { id: string; label: string }) => void;
  activeIndex?: number;
  isLoading?: boolean;
}

const SkeletonItem = () => (
  <div className="flex items-center gap-2 px-3 py-2">
    <AntdSkeleton.Avatar active size={24} shape="circle" />
    <AntdSkeleton.Input active style={{ height: '16px', width: '120px' }} />
  </div>
);

export const SuggestionList = forwardRef<HTMLDivElement, SuggestionListProps>(
  ({ items, command, activeIndex = 0, isLoading = false }, ref) => {
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

    // Scroll active item into view when navigating with keyboard
    useEffect(() => {
      itemRefs.current[activeIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }, [activeIndex]);

    if (isLoading) {
      return (
        <div
          ref={ref}
          className="bg-white border rounded-lg"
          data-testid="suggestion-list-skeleton"
        >
          <SkeletonItem />
          <SkeletonItem />
          <SkeletonItem />
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div ref={ref} className="bg-white border rounded-lg">
          <div className="px-3 py-2 text-gray-500 text-sm">No users found</div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className="bg-white border overflow-y-auto rounded-lg max-h-[300px]"
      >
        {items.map((user, index) => (
          <Button
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            type="text"
            data-testid={`suggestion-list-item-${user.name.toLowerCase().replaceAll(' ', '-')}`}
            key={user.id}
            className={`flex items-center gap-2 w-full px-3 py-2 text-sm justify-start text-left ${
              index === activeIndex ? 'bg-gray-100' : 'hover:bg-gray-100'
            }`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => command({ id: user.id, label: user.name })}
          >
            <UserRenderer userId={user.id} size="small" />
          </Button>
        ))}
      </div>
    );
  }
);

SuggestionList.displayName = 'SuggestionList';
